import React, { forwardRef, useCallback } from 'react';
import { useDiary, ACTIONS, formatTime } from './DiaryContext';
import { useDrag } from './DragContext';
import { SHELF_STAFF_ID } from './DragOverlay';

const DRAG_THRESHOLD = 6;

/**
 * DiaryShelf - Floating pill bar for temporarily parking appointments.
 * Inspired by Apple's Dynamic Island: appears only when needed, compact, interactive.
 */
const DiaryShelf = forwardRef(function DiaryShelf({ onEventClick }, ref) {
  const { state, dispatch } = useDiary();
  const { dragState, startDrag } = useDrag();
  const { parkedEvents } = state;

  const isShelfDropTarget = dragState?.mode === 'move' && dragState?.currentStaffId === SHELF_STAFF_ID;
  const isDragging = dragState?.mode === 'move';

  const handleRestore = useCallback((eventId, e) => {
    e.stopPropagation();
    dispatch({ type: ACTIONS.UNPARK_EVENT, payload: { eventId, restore: true } });
  }, [dispatch]);

  const handlePillClick = useCallback((parkedItem, e) => {
    if (onEventClick) onEventClick(parkedItem.event);
  }, [onEventClick]);

  const handlePillPointerDown = useCallback((parkedItem, e) => {
    // Start drag from shelf
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX || (e.touches && e.touches[0].clientX);
    const startY = e.clientY || (e.touches && e.touches[0].clientY);
    let moved = false;

    const onMove = (me) => {
      const mx = me.clientX || (me.touches && me.touches[0].clientX);
      const my = me.clientY || (me.touches && me.touches[0].clientY);
      const dx = mx - startX;
      const dy = my - startY;
      if (!moved && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) {
        moved = true;
        startDrag(parkedItem.event, SHELF_STAFF_ID, startX, startY, startY - rect.top);
      }
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      if (!moved) {
        handlePillClick(parkedItem, e);
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  }, [startDrag, handlePillClick]);

  // Don't render if nothing parked and not dragging
  if (parkedEvents.length === 0 && !isDragging) return <div ref={ref} className="dv2-shelf-zone" />;

  return (
    <div ref={ref} className={`dv2-shelf-zone ${isDragging ? 'dv2-shelf-zone--drag-active' : ''}`}>
      {/* Drop zone - shown as soon as any drag starts */}
      {isDragging && parkedEvents.length < 3 && (
        <div className={`dv2-shelf-dropzone ${isShelfDropTarget ? 'dv2-shelf-dropzone--hover' : ''}`}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            <path d="M3.5 6.5L7 10L10.5 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12.5H12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <span>Drop booking here</span>
        </div>
      )}

      {/* Parked pills */}
      {parkedEvents.length > 0 && (
        <div className="dv2-shelf">
          {parkedEvents.map(p => {
            const chainCount = p.chainEvents ? p.chainEvents.length : 0;
            const totalDur = chainCount > 1
              ? Math.max(...p.chainEvents.map(e => e.endMinutes)) - Math.min(...p.chainEvents.map(e => e.startMinutes))
              : p.event.endMinutes - p.event.startMinutes;
            const h = Math.floor(totalDur / 60);
            const m = totalDur % 60;
            const durLabel = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
            return (
              <div
                key={p.event.id}
                className="dv2-shelf-pill"
                onPointerDown={(e) => handlePillPointerDown(p, e)}
              >
                <span className="dv2-shelf-pill-dot" style={{ background: p.event.diaryColour || '#D6D5EC' }} />
                <div className="dv2-shelf-pill-info">
                  <span className="dv2-shelf-pill-name">
                    {p.event.clientName || p.event.title}
                  </span>
                  <span className="dv2-shelf-pill-meta">
                    {formatTime(p.event.startMinutes)} · {durLabel}
                    {chainCount > 1 && (
                      <span className="dv2-shelf-pill-chain">
                        <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                          <path d="M2.5 1H7.5M2.5 9H7.5M3 1V3.5L5 5L3 6.5V9M7 1V3.5L5 5L7 6.5V9" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {chainCount}
                      </span>
                    )}
                  </span>
                </div>
                <button
                  className="dv2-shelf-pill-close"
                  onClick={(e) => handleRestore(p.event.id, e)}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default DiaryShelf;
